import { useState } from "react";
import { deepEvents } from "@/mocks/chigua";
import SectionHeading from "./SectionHeading";

export default function DeepTimeline() {
  const event = deepEvents[0];
  const [active, setActive] = useState(0);
  const total = event.steps.length;
  const step = event.steps[active];

  const go = (delta: number) => setActive((prev) => (prev + delta + total) % total);

  return (
    <section id="deep" className="scroll-mt-20 py-14 md:py-20">
      <div className="chigua-container mx-auto">
        <SectionHeading
          eyebrow="深度时间线"
          title="一颗瓜，从头吃到尾"
          desc="编辑部的深度栏目用关键节点还原事件全貌。点击左侧每个节点，即可展开该阶段的详细复盘。"
        />

        <div className="mt-8 overflow-hidden rounded-[32px] border border-background-200 bg-background-50">
          {/* banner */}
          <div className="relative aspect-[16/9] w-full sm:aspect-[21/9]">
            <img src={event.image} alt={event.imageAlt} className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-foreground-950/20 to-transparent"></div>
            <div className="absolute bottom-0 left-0 flex flex-wrap items-center gap-2 p-5 md:p-7">
              <span className="rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-background-50">{event.category}</span>
              <span className="rounded-full bg-background-50/25 px-3 py-1 text-xs text-background-50 backdrop-blur">{event.reading}</span>
            </div>
          </div>

          {/* body */}
          <div className="p-5 md:p-8">
            <h3 className="max-w-3xl font-display text-xl leading-snug text-foreground-900 md:text-2xl">{event.title}</h3>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-[15px]">{event.lede}</p>
            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-400">
              <span className="inline-flex items-center gap-1.5"><i className="ri-quill-pen-fill text-primary-500"></i>{event.source}</span>
              <span className="inline-flex items-center gap-1.5"><i className="ri-refresh-line text-accent-600"></i>{event.updated}</span>
            </div>

            <div className="mt-7 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
              {/* step nav */}
              <ol className="relative space-y-0">
                {event.steps.map((s, i) => {
                  const done = i <= active;
                  const isLast = i === total - 1;
                  return (
                    <li key={s.title} className="relative flex gap-3 pb-5">
                      {!isLast && (
                        <span
                          className={`absolute left-[13px] top-7 h-[calc(100%-1.4rem)] w-0.5 rounded-full ${
                            i < active ? "bg-primary-400" : "bg-background-200"
                          }`}
                          aria-hidden="true"
                        ></span>
                      )}
                      <button
                        type="button"
                        onClick={() => setActive(i)}
                        aria-current={i === active ? "step" : undefined}
                        className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-bold transition-all ${
                          i === active
                            ? "bg-primary-500 text-background-50 ring-4 ring-primary-100"
                            : done
                            ? "bg-primary-200 text-primary-700"
                            : "bg-background-200 text-foreground-400"
                        }`}
                      >
                        {done && i !== active ? <i className="ri-check-line"></i> : i + 1}
                      </button>
                      <button
                        type="button"
                        onClick={() => setActive(i)}
                        className={`min-w-0 flex-1 rounded-2xl border px-3 py-2 text-left transition-all ${
                          i === active
                            ? "border-primary-200 bg-primary-50"
                            : "border-transparent hover:bg-background-100"
                        }`}
                      >
                        <span className={`block text-xs ${i === active ? "text-primary-600" : "text-foreground-400"}`}>{s.time}</span>
                        <span className={`block truncate text-sm font-semibold ${i === active ? "text-primary-800" : "text-foreground-700"}`}>
                          {s.title}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ol>

              {/* active detail */}
              <div className="rounded-3xl bg-background-100 p-5 md:p-7">
                <div className="flex items-center justify-between">
                  <span className="rounded-full bg-background-50 px-3 py-1 text-xs font-semibold text-primary-700">
                    第 {active + 1} / {total} 节点
                  </span>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => go(-1)} aria-label="上一个节点" className="grid h-9 w-9 place-items-center rounded-full bg-background-50 text-foreground-600 transition-colors hover:bg-primary-100 hover:text-primary-700">
                      <i className="ri-arrow-left-line"></i>
                    </button>
                    <button type="button" onClick={() => go(1)} aria-label="下一个节点" className="grid h-9 w-9 place-items-center rounded-full bg-background-50 text-foreground-600 transition-colors hover:bg-primary-100 hover:text-primary-700">
                      <i className="ri-arrow-right-line"></i>
                    </button>
                  </div>
                </div>
                <p className="mt-4 font-display text-2xl text-primary-600">{step.time}</p>
                <h4 className="mt-1 font-display text-xl text-foreground-900">{step.title}</h4>
                <p className="mt-3 leading-relaxed text-foreground-600">{step.text}</p>
                <div className="mt-5 flex items-center gap-2 rounded-2xl bg-background-50 p-3 text-xs text-foreground-500">
                  <i className="ri-focus-3-line text-accent-600"></i>
                  编辑视角：此节点已结合多方信源交叉核实，标注事实而非观点。
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}